import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

import {
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit,
  ViewChild,
} from '@angular/core';

import { SnCamera, SnDetectionFrame } from '@lib/api/entities';
import { PursuitService } from '@lib/cameras/services/pursuit.service';

import { MapSentrySearchService } from '../../services/map-sentry.service';
import { SentryActionsService } from '../../services/sentry-actions.service';
import { AiBoxComponent } from './../ai-box/ai-box.component';

@Component({
  selector: 'fs-sentry-search-pagination',
  templateUrl: './sentry-search-pagination.component.html',
  styleUrls: ['./sentry-search-pagination.component.scss'],
  providers: [SentryActionsService],
})
export class SentrySearchPaginationComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(AiBoxComponent) public aiBoxChild: AiBoxComponent;
  @ViewChild(ToastContainerDirective, { static: true })
  public toastContainer: ToastContainerDirective;

  @Input() public pursuitMode: boolean;
  @Input() public item: SnDetectionFrame;
  @Input() public creationDate: string;
  @Input() public id: string;
  @Input() public queryParams: any;
  @Input() public allowGoTo: boolean = true;

  public showHover: boolean;

  public data: any;
  public index = 0;
  public matches: any[];
  public camera: SnCamera;
  public canvas: HTMLCanvasElement;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _mapSentryService: MapSentrySearchService,
    private readonly _sentryActionsService: SentryActionsService,
    public readonly _pursuitAPI: PursuitService,
    private readonly _toastrService: ToastrService,
  ) {}

  public ngOnInit(): void {
    this.matches = this._sortMatchingDetections(this.setQueryParams());
    this._setPagination();
    this._cd.detectChanges();
    this._toastrService.overlayContainer = this.toastContainer;
  }

  public ngAfterViewInit(): void {
    this._resizeImage();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public mouseHover(item: any): void {
    if (this.pursuitMode) {
      this._pursuitAPI.hoverRadar(item.cameraId);
      this._pursuitAPI.hoverMap(item.cameraId);
    }
  }

  public mouseOut(item: any): void {
    if (this.pursuitMode) {
      this._pursuitAPI.leaveRadar(item.cameraId);
      this._pursuitAPI.leaveMap(item.cameraId);
    }
  }

  public setQueryParams(): any {
    return this.queryParams?.__drawerData__ ? this.queryParams?.__drawerData__ : this.queryParams;
  }

  public next(): void {
    this.index = this.index + 1;
    this._setPagination();
    this._resizeImage();
    this._onPageChange();
  }

  public preview(): void {
    this.index = this.index - 1;
    this._setPagination();
    this._resizeImage();
    this._onPageChange();
  }

  public goToCamera(item: any, data: any): void {
    if (this.pursuitMode) {
      this._pursuitAPI.setNewCam(item.cameraId, item.timestamp);
    } else {
      this._mapSentryService.getCameraAndGoTo(item, data, this.queryParams.__drawerData__);
    }
  }

  public setViewClip(item: any, data: any): void {
    this._sentryActionsService.setViewClip(item, data);
  }

  public viewDetectionDetails(): void {
    this._sentryActionsService.viewDetectionDetails(
      this.item,
      this.data,
      this.queryParams.__drawerData__,
    );
  }

  public sendToVault(url: string): void {
    this._sentryActionsService.sendToVault(url);
  }

  public goToCameraPursuit(item: any): void {
    this._pursuitAPI.setNewCam(item.cameraId);
    this._cd.detectChanges();
  }

  public selectChannel(url: any): void {
    this._sentryActionsService.selectChannel(url);
  }

  public incorrectSentryResult(detectionItem: any, cameraDetections: any): void {
    this._sentryActionsService.incorrectSentryResult(
      detectionItem,
      cameraDetections,
      this.queryParams.__drawerData__,
    );
  }

  public reported(): boolean {
    return this._sentryActionsService.getReported();
  }

  public reporting(): boolean {
    return this._sentryActionsService.getReporting();
  }

  private _resizeImage(): void {
    const canvas: any = document.getElementById(this.item.id);
    const context: CanvasRenderingContext2D = canvas?.getContext('2d');
    const imageObj = new Image();
    imageObj.crossOrigin = 'Anonymous';
    if (!canvas) {
      return;
    }

    canvas.width = +this.data.box.width + 100;
    canvas.height = +this.data.box.height + 100;

    const resolutionBoxWidth = this.item.resolutionBox?.width || this.item.resolution.width;
    const resolutionBoxHeight = this.item.resolutionBox?.height || this.item.resolution.height;

    const widthPercentage = this.data.box.width / resolutionBoxWidth;
    const heightPercentage = this.data.box.height / resolutionBoxHeight;

    const widthPixels = widthPercentage * this.item.resolution.width;
    const heigthPixels = heightPercentage * this.item.resolution.height;

    const startX = this.data.box.startX / resolutionBoxWidth;
    const startY = this.data.box.startY / resolutionBoxHeight;

    const startXPixels = startX * this.item.resolution.width;
    const startYPixels = startY * this.item.resolution.height;

    const maxX = this.item.resolution.width;
    const maxY = this.item.resolution.height;
    const maxWidth = maxX - startXPixels;
    const maxHeight = maxY - startYPixels;

    const height = Math.min(heigthPixels, maxHeight);
    const width = Math.min(widthPixels, maxWidth);

    canvas.width = width;
    canvas.height = height;

    imageObj.onload = () => {
      context.drawImage(imageObj, startXPixels, startYPixels, width, height, 0, 0, width, height);
    };
    imageObj.src = this.item.detectionsImageUrl;
    this._cd.detectChanges();
  }

  private _onPageChange(): void {
    const img: any = document.getElementById(this.item.id);
    img.classList.remove('animated');
    void img.offsetWidth;
    img.classList.add('animated');
    navigator.vibrate(100);
    this._cd.detectChanges();
  }

  private _setPagination(): void {
    this.data = this.matches[this.index];
    this._cd.detectChanges();
  }

  private _sortMatchingDetections(params: any): any[] {
    if (params.events__label__in && params.events__label__in !== null) {
      this.item.events.sort((a, b) => {
        const label = Array.isArray(params.events__label__in)
          ? params.events__label__in[0]
          : params.events__label__in;
        if (a.label === label && b.label === label) {
          return 0;
        }
        if (a.label === label) {
          return -1;
        }
        if (b.label === label) {
          return 1;
        }
        return 0;
      });
      return this.item.events;
    }
    if (params.events__label__in === null || params.events__label__in === '') {
      return this.item.events.filter((event) => {
        const pass = true;
        return pass;
      });
    }
    this.item.detections.forEach((detection) => {
      detection.relevance = 0;
      if (params.label__in) {
        const label = Array.isArray(params.label__in) ? params.label__in[0] : params.label__in;
        detection.relevance += label === detection.label ? 100 : 0;
        switch (label) {
          case 'person':
            const colorTop = params.metadata__color_top__in;
            const colorBottom = params.metadata__color_bottom__in;
            if (colorTop || colorBottom) {
              if (colorTop) {
                detection.relevance +=
                  detection.metadata.colorTop?.label === colorTop
                    ? detection.metadata.colorTop?.confidence
                    : 0;
              }
              if (colorBottom) {
                detection.relevance +=
                  detection.metadata.colorBottom?.label === colorBottom
                    ? detection.metadata.colorBottom?.confidence
                    : 0;
              }
            } else {
              const attributes = ['has_backpack', 'has_bag', 'has_hat', 'is_male'];
              attributes.forEach((attribute) => {
                if (params[`metadata__attributes__${attribute}__confidence__lte`]) {
                  const match = detection.metadata.attributes?.find((att) => {
                    return att.label === attribute && att.confidence < 50;
                  });
                  if (match) {
                    detection.relevance += 100 - match.confidence;
                  }
                }
                if (params[`metadata__attributes__${attribute}__confidence__gte`]) {
                  const match = detection.metadata.attributes?.find((att) => {
                    return att.label === attribute && att.confidence < 50;
                  });
                  if (match) {
                    detection.relevance += match.confidence;
                  }
                }
              });
            }
            break;
          case 'car':
            if (params.metadata__car_color__in) {
              const foundCar = detection.metadata.carColor?.find((color) => {
                return color.label === params.metadata__car_color__in;
              });
              if (foundCar) {
                detection.relevance += foundCar.confidence;
              }
            }
            if (params.metadata__vehicle_type__in) {
              const foundCar = detection.metadata.vehicleType?.find((type) => {
                return type.label === params.metadata__vehicle_type__in;
              });
              if (foundCar) {
                detection.relevance += foundCar.confidence;
              }
            }
            if (params.metadata__car_make__in) {
              const foundCar = detection.metadata.carMake?.find((make) => {
                return make.label === params.metadata__car_make__in;
              });
              if (foundCar) {
                detection.relevance += foundCar.confidence;
              }
            }
            break;
        }
      }
    });
    this.item.detections.sort((a, b) => {
      return b.relevance - a.relevance;
    });
    return this.item.detections;
  }
}
